// src/pages/PublicCanvasEditor.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Importando componentes do MUI
import { 
    Container, 
    Paper, 
    TextField, 
    Typography, 
    Button, 
    Box, 
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import { Save as SaveIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';

// --- COMPONENTE DO BLOCO DO CANVAS ---
const CanvasBlock = ({ title, content, onChange, fieldName, readOnly }) => {
    return (
        <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: readOnly ? '#f9f9f9' : '#fff' }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ borderBottom: '1px solid #ddd', pb: 1, fontSize: '1rem', fontWeight: 'bold' }}>
                {title}
            </Typography>
            <TextField
                value={content || ''}
                onChange={(e) => onChange(fieldName, e.target.value)}
                multiline
                rows={8}
                variant="outlined"
                fullWidth
                disabled={readOnly} // << Trava a edição se for apenas visualização
                sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%' } }}
                placeholder={readOnly ? 'Sem conteúdo.' : `Adicione ${title.toLowerCase()}...`}
            />
        </Paper>
    );
};

const PublicCanvasEditor = () => {
    const { shareToken } = useParams(); // Pega o token gigante da URL
    const [canvas, setCanvas] = useState(null);
    const [accessLevel, setAccessLevel] = useState('none');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // ==========================================================
    // BUSCA OS DADOS NA ROTA PÚBLICA
    // ==========================================================
    const fetchPublicCanvas = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get(`/public/canvas/${shareToken}`);
            setCanvas(response.data.canvas || response.data); // Depende de como o seu backend está retornando, isso cobre ambos
            setAccessLevel(response.data.access || response.data.shareAccess);
        } catch (err) {
            setError('Este link é inválido, expirou ou o compartilhamento foi desativado pelo proprietário.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [shareToken]);

    useEffect(() => {
        fetchPublicCanvas();
    }, [fetchPublicCanvas]);

    // Atualiza os campos na tela
    const handleChange = (fieldName, value) => {
        if (accessLevel !== 'edit') return; // Segurança extra no frontend
        setCanvas(prevCanvas => ({ ...prevCanvas, [fieldName]: value }));
    };

    // ==========================================================
    // SALVA OS DADOS NA ROTA PÚBLICA (Se tiver permissão)
    // ==========================================================
    const handleSave = async () => {
        if (accessLevel !== 'edit') return;
        
        setIsSaving(true);
        const updatedData = {
            keyPartners: canvas.keyPartners,
            keyActivities: canvas.keyActivities,
            keyResources: canvas.keyResources,
            valuePropositions: canvas.valuePropositions,
            customerRelationships: canvas.customerRelationships,
            channels: canvas.channels,
            customerSegments: canvas.customerSegments,
            costStructure: canvas.costStructure,
            revenueStreams: canvas.revenueStreams,
            title: canvas.name 
        };

        try {
            await api.put(`/public/canvas/${shareToken}`, updatedData);
            setSnackbar({ open: true, message: 'Alterações salvas com sucesso!', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Erro ao salvar as alterações.', severity: 'error' });
            console.error(err); 
        } finally {
            setIsSaving(false);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    // ==========================================================
    // GERAÇÃO DE PDF
    // ==========================================================
    const handleGeneratePdf = () => {
        setIsGeneratingPdf(true);
        const canvasContainer = document.getElementById('canvas-container');

        html2canvas(canvasContainer, { 
            scale: 2,
            logging: false,
            useCORS: true,
        }).then((canvasScreenshot) => {
            const imgData = canvasScreenshot.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 40;

            pdf.setFontSize(22);
            pdf.text(canvas.name || 'Business Model Canvas', pdfWidth / 2, margin, { align: 'center' });

            const imgProps = pdf.getImageProperties(imgData);
            const aspectRatio = imgProps.height / imgProps.width;
            const imgWidth = pdfWidth - margin * 2;
            const imgHeight = imgWidth * aspectRatio;

            pdf.addImage(imgData, 'PNG', margin, margin + 20, imgWidth, imgHeight);

            pdf.setFontSize(10);
            const dateStr = `Relatório gerado em: ${new Date().toLocaleDateString('pt-BR')}`;
            pdf.text(dateStr, margin, pdfHeight - margin / 2);

            pdf.save(`canvas-${canvas.name}.pdf`);
        }).finally(() => {
            setIsGeneratingPdf(false);
        });
    };

    // --- RENDERIZAÇÃO DE ESTADOS (Carregando / Erro) ---
    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" height="100vh"><CircularProgress /></Box>;
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh" bgcolor="#f5f5f5">
                <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
                    <Typography variant="h5" color="error" gutterBottom>Acesso Negado</Typography>
                    <Typography color="textSecondary">{error}</Typography>
                </Paper>
            </Box>
        );
    }

    if (!canvas) {
        return <Typography sx={{ p: 4 }}>Canvas não encontrado.</Typography>;
    }

    const isReadOnly = accessLevel !== 'edit';

    return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', bgcolor: '#f0f2f5', minHeight: '100vh' }}>
            <Container 
                maxWidth="xl" 
                sx={{ 
                    py: 4,
                    transform: 'scale(0.85)', // Um leve ajuste no scale para caber melhor em telas normais
                    transformOrigin: 'top center',
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} p={2} bgcolor="#fff" borderRadius={2} boxShadow="0 2px 10px rgba(0,0,0,0.05)">
                    <div>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ m: 0, fontWeight: 'bold' }}>
                            {canvas.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {isReadOnly ? "Visualização Pública (Apenas Leitura)" : "Modo de Edição Colaborativa"}
                        </Typography>
                    </div>
                    <Box>
                        {/* Se o link for de edição, mostra o botão de Salvar */}
                        {!isReadOnly && (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                size="large"
                                startIcon={<SaveIcon />}
                                onClick={handleSave} 
                                disabled={isSaving}
                                sx={{ mr: 2 }}
                            >
                                {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                            </Button>
                        )}
                        <Button 
                            variant="outlined" 
                            color="secondary" 
                            size="large" 
                            startIcon={<PdfIcon />} 
                            onClick={handleGeneratePdf} 
                            disabled={isGeneratingPdf}
                        >
                            {isGeneratingPdf ? 'Gerando...' : 'Baixar PDF'}
                        </Button>
                    </Box>
                </Box>

                <Box id="canvas-container" display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                        <Box flex={1}><CanvasBlock title="Key Partners" content={canvas.keyPartners} onChange={handleChange} fieldName="keyPartners" readOnly={isReadOnly} /></Box>
                        <Box flex={1} display="flex" flexDirection="column" gap={2}>
                            <Box flex={1}><CanvasBlock title="Key Activities" content={canvas.keyActivities} onChange={handleChange} fieldName="keyActivities" readOnly={isReadOnly} /></Box>
                            <Box flex={1}><CanvasBlock title="Key Resources" content={canvas.keyResources} onChange={handleChange} fieldName="keyResources" readOnly={isReadOnly} /></Box>
                        </Box>
                        <Box flex={1}><CanvasBlock title="Value Proposition" content={canvas.valuePropositions} onChange={handleChange} fieldName="valuePropositions" readOnly={isReadOnly} /></Box>
                        <Box flex={1} display="flex" flexDirection="column" gap={2}>
                            <Box flex={1}><CanvasBlock title="Customer Relationship" content={canvas.customerRelationships} onChange={handleChange} fieldName="customerRelationships" readOnly={isReadOnly} /></Box>
                            <Box flex={1}><CanvasBlock title="Channels" content={canvas.channels} onChange={handleChange} fieldName="channels" readOnly={isReadOnly} /></Box>
                        </Box>
                        <Box flex={1}><CanvasBlock title="Customer Segments" content={canvas.customerSegments} onChange={handleChange} fieldName="customerSegments" readOnly={isReadOnly} /></Box>
                    </Box>
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                        <Box flex={1}><CanvasBlock title="Cost Structure" content={canvas.costStructure} onChange={handleChange} fieldName="costStructure" readOnly={isReadOnly} /></Box>
                        <Box flex={1}><CanvasBlock title="Revenue Streams" content={canvas.revenueStreams} onChange={handleChange} fieldName="revenueStreams" readOnly={isReadOnly} /></Box>
                    </Box>
                </Box>

                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default PublicCanvasEditor;